import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop } from 'lodash';
import { translate } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { useBreakpoints } from '@fiverr-private/hooks';
import { Container, Stack } from '@fiverr-private/layout_components';
import { TimesButton } from '@fiverr-private/button';
import SubmitButton from '../../../components/SubmitButton';
import { SUBMIT_BUTTON_TYPE } from '../../../utils/constants';
import { useTypingPlaceholder } from '../useTypingPlaceholder';
import { HARDCODED_TRENDING, getHardcodedTrending } from '../hardcodedTrending';
import { resolveSubCategoryId } from '../resolveSubCategoryId';
import { aiPillClearButtonClass, aiPillFieldClass, aiPillInputClass } from '../styles.ve.css';

const AiPillSearchField = ({
    search,
    subCategoryId,
    formInputRef,
    handleChange,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleClear,
    handleClose,
    isOpen,
    submitButtonText,
    hideClearButton,
}) => {
    const { locale, isTouch } = getContext();
    const inputRef = useRef(null);
    const breakpoints = useBreakpoints();
    const isMobile = breakpoints.default && !breakpoints.md;
    const defaultPlaceholder = translate(isMobile ? 'search_bar.placeholder_short' : 'search_bar.placeholder_long', {
        locale,
    });

    const exampleQueries = useMemo(() => getHardcodedTrending(locale).map(({ value }) => value), [locale]);

    const startIndex = useMemo(() => {
        const currentSubCategoryId = subCategoryId ?? resolveSubCategoryId();

        return Math.max(
            HARDCODED_TRENDING.findIndex((query) => query.subCategoryId === currentSubCategoryId),
            0
        );
    }, [subCategoryId]);

    const {
        typedText,
        handleMouseEnter,
        handleMouseLeave,
        handleFocus: onTypingFocus,
        handleBlur: onTypingBlur,
        handleInput: onTypingInput,
    } = useTypingPlaceholder({
        texts: exampleQueries,
        startIndex,
        instant: !!isTouch,
        disabled: isMobile,
    });

    const placeholder =
        typedText === null
            ? defaultPlaceholder
            : translate('search_bar.ai_pill.placeholder_example', { locale, params: { query: typedText } });

    const onFocus = (e) => {
        onTypingFocus();
        handleFocus(e);
    };

    const onBlur = (e) => {
        onTypingBlur();
        handleBlur(e);
    };

    const onInput = (e) => {
        onTypingInput();
        handleChange(e);
    };

    const onClearAndClose = (e) => {
        // Same detached-target guard as `onClear`; blur instead of refocusing so focus doesn't reopen the panel.
        e.stopPropagation();
        handleClear();
        inputRef.current?.blur();
        handleClose();
    };

    return (
        <Stack alignItems="center" gap="2">
            <Container
                ref={formInputRef}
                className={aiPillFieldClass}
                display="flex"
                alignItems="center"
                width="100%"
                gap="4"
                paddingX="2"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Container
                    as="input"
                    ref={inputRef}
                    type="search"
                    name="search"
                    autoComplete="off"
                    aria-label={defaultPlaceholder}
                    className={classNames('ai-pill-search-bar', aiPillInputClass)}
                    placeholder={placeholder}
                    value={search}
                    onInput={onInput}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onKeyDown={handleKeyDown}
                    flexGrow="1"
                />
                <SubmitButton type={SUBMIT_BUTTON_TYPE.PILL} submitButtonText={submitButtonText} iconOnly />
            </Container>
            {!hideClearButton && isOpen && isMobile && (
                <TimesButton
                    type="button"
                    className={aiPillClearButtonClass}
                    ariaLabel={translate('search_bar.clear', { locale })}
                    shape="circle"
                    size="sm"
                    onClick={onClearAndClose}
                />
            )}
        </Stack>
    );
};

AiPillSearchField.propTypes = {
    search: PropTypes.string,
    subCategoryId: PropTypes.number,
    formInputRef: PropTypes.shape({ current: PropTypes.any }),
    handleChange: PropTypes.func.isRequired,
    handleFocus: PropTypes.func.isRequired,
    handleBlur: PropTypes.func.isRequired,
    handleKeyDown: PropTypes.func.isRequired,
    handleClear: PropTypes.func.isRequired,
    handleClose: PropTypes.func,
    isOpen: PropTypes.bool,
    submitButtonText: PropTypes.node,
    hideClearButton: PropTypes.bool,
};

AiPillSearchField.defaultProps = {
    search: '',
    hideClearButton: false,
    handleClose: noop,
    isOpen: false,
};

export { AiPillSearchField };
export default AiPillSearchField;
